import { useEffect, useRef, useState, type FormEvent, type ReactNode } from "react";
import type { StateSpec } from "../lib/states";

const MAX = 280;

export interface Note {
  tag: string;
  text: string;
  tone: "ok" | "bad";
}

interface Props {
  spec: StateSpec;
  widget: ReactNode;
  configError: string | null;
  cooldownUntil: number;
  /** Outcome-specific note that wins over the state's own (e.g. queued for review). */
  noteOverride: Note | null;
  onSubmit: (name: string, message: string) => void;
  onAct: (act: string) => void;
}

export default function ComposeForm({
  spec,
  widget,
  configError,
  cooldownUntil,
  noteOverride,
  onSubmit,
  onAct,
}: Props) {
  const [name, setName] = useState("");
  const [message, setMessage] = useState("");
  const [note, setNote] = useState<Note | null>(null);
  const [left, setLeft] = useState(0);
  const messageRef = useRef<HTMLTextAreaElement>(null);

  // An outcome-specific note wins over the state's own; a blocked-click note is
  // transient on top of both.
  useEffect(() => {
    setNote(noteOverride ?? spec.note ?? null);
    if (spec.id === "submitted" || noteOverride) setMessage("");
  }, [spec, noteOverride]);

  useEffect(() => {
    if (!spec.countdown) return;
    const tick = () => setLeft(Math.max(0, cooldownUntil - Date.now()));
    tick();
    const t = window.setInterval(tick, 1000);
    return () => window.clearInterval(t);
  }, [spec.countdown, cooldownUntil]);

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!spec.submitEnabled) {
      setNote({ tag: "Not yet", text: spec.blockedReason ?? "Not ready yet.", tone: "bad" });
      return;
    }
    if (!message.trim()) {
      setNote({
        tag: "The wall",
        text: "A message is required. Write something and post again.",
        tone: "bad",
      });
      messageRef.current?.focus();
      return;
    }
    setNote(null);
    onSubmit(name.trim(), message.trim());
  }

  const seconds = Math.ceil(left / 1000);
  const okNote = note?.tone === "ok" ? note : null;
  const badNote = note?.tone === "bad" ? note : null;

  return (
    <section className="compose" id="compose" aria-labelledby="compose-h">
      <div className="section-head">
        <h2 className="eyebrow" id="compose-h">
          Add to the wall
        </h2>
      </div>

      {/*
        The fields are their own <form>; the slot, privacy line and submit
        button are siblings inside the same flex column, so the visual order is
        unchanged. This is not cosmetic: the ShieldWall widget renders its own
        <form> for the challenge answer, and HTML forbids nested forms — the
        browser silently drops the inner one and the challenge stops submitting.
        The submit button reaches back in with the `form` attribute.
      */}
      <div className="form">
        <form id="wall-form" className="form__fields" onSubmit={handleSubmit} noValidate>
          <div className="field">
          <label className="label" htmlFor="name">
            Name <span className="label__note">optional</span>
          </label>
          <input
            className="input"
            id="name"
            type="text"
            maxLength={32}
            autoComplete="nickname"
            placeholder="Anonymous"
            spellCheck={false}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>

        <div className="field">
          <div className="label-row">
            <label className="label" htmlFor="message">
              Message
            </label>
            <span className="counter" data-near={message.length > MAX - 40}>
              {message.length}/{MAX}
            </span>
          </div>
          <textarea
            className="input input--area"
            id="message"
            ref={messageRef}
            rows={3}
            maxLength={MAX}
            required
            aria-describedby="messageHint"
            placeholder="Say something worth reading."
            value={message}
            onChange={(e) => setMessage(e.target.value)}
          />
          <p className="hint" id="messageHint">
            Goes up in public, under your name or without one.
          </p>
          </div>
        </form>

        {/* Reserved slot. Fixed height in every state. Never moves. */}
        <div className="slot" data-state={spec.slot}>
          <span className="slot__rail" aria-hidden="true" />
          <div className="slot__body">
            <p className="slot__status" role="status">
              {!spec.alert && (
                <>
                  <span className="tag">{spec.tag}</span>
                  {spec.status}
                  {spec.countdown && (
                    <span className="chal__txt" aria-hidden="true">
                      {Math.floor(seconds / 60)}:{String(seconds % 60).padStart(2, "0")} left
                    </span>
                  )}
                </>
              )}
            </p>
            <p className="slot__status slot__status--bad" role="alert">
              {spec.alert && (
                <>
                  <span className="tag">{spec.tag}</span>
                  {spec.status}
                  {spec.countdown && (
                    <span className="chal__txt" aria-hidden="true">
                      {Math.floor(seconds / 60)}:{String(seconds % 60).padStart(2, "0")} left
                    </span>
                  )}
                </>
              )}
            </p>

            {/* The real widget lives here. Hidden, not unmounted, once resolved —
                unmounting would discard the behavioural signals it has collected. */}
            <div className="slot__mount" hidden={!spec.showWidget}>
              {widget}
            </div>

            {configError && (
              <p className="slot__status slot__status--bad" role="alert">
                <span className="tag">Not configured</span>
                {configError}
              </p>
            )}

            {spec.acts.length > 0 && (
              <div className="slot__acts">
                {spec.acts.map((a) => (
                  <button
                    key={a.id}
                    type="button"
                    className={`btn btn--${a.variant}`}
                    onClick={() => onAct(a.id)}
                  >
                    {a.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <p className="privacy">
          <svg className="privacy__i" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
            <circle cx="8" cy="8" r="7" />
            <path d="M8 7.2v4.2M8 4.6v1.2" />
          </svg>
          <span>
            This page measures how you move, type, and scroll to tell people from bots.{" "}
            <a href="#privacy-detail">What that means</a>.
          </span>
        </p>

        <button
          className="btn btn--primary"
          type="submit"
          form="wall-form"
          aria-disabled={!spec.submitEnabled}
          data-busy={spec.submitBusy}
        >
          <span className="btn__label">{spec.submitLabel}</span>
          <span className="btn__spin" aria-hidden="true" />
        </button>

        <p className="form-note" role="status" data-tone={okNote ? "ok" : undefined}>
          {okNote && (
            <>
              <span className="tag">{okNote.tag}</span>
              {okNote.text}
            </>
          )}
        </p>
        <p className="form-note" role="alert" data-tone={badNote ? "bad" : undefined}>
          {badNote && (
            <>
              <span className="tag">{badNote.tag}</span>
              {badNote.text}
            </>
          )}
        </p>
      </div>
    </section>
  );
}
